<template>
  <div class="card policy-chart-card h-100">
    <div class="card-header pb-0">
      <div class="d-flex align-items-center justify-content-between">
        <h6 class="mb-0">{{ title }}</h6>
        <span v-if="count && hasData" class="badge bg-gradient-info">{{ count }}</span>
      </div>
    </div>
    <div class="card-body policy-chart-body">
      <div v-if="hasData" class="doughnut-container" @mouseleave="hoveredIndex = -1">
        <!-- 2-Column Legend -->
        <div class="doughnut-legend">
          <div class="doughnut-legend-col">
            <div
              v-for="({ label, color, amount }, index) of column1Items"
              :key="'l1-' + index"
              class="doughnut-legend-item"
              :class="{ 'is-hovered': hoveredIndex === index, 'is-dimmed': hoveredIndex !== -1 && hoveredIndex !== index }"
              @mouseenter="hoveredIndex = index"
            >
              <span class="doughnut-dot" :style="{ backgroundColor: color }"></span>
              <span class="doughnut-label" :title="label">{{ label }}</span>
              <span v-if="amount > 0" class="doughnut-val">{{ amount }}</span>
            </div>
          </div>
          <div class="doughnut-legend-col">
            <div
              v-for="({ label, color, amount, globalIndex }, index) of column2Items"
              :key="'l2-' + index"
              class="doughnut-legend-item"
              :class="{ 'is-hovered': hoveredIndex === globalIndex, 'is-dimmed': hoveredIndex !== -1 && hoveredIndex !== globalIndex }"
              @mouseenter="hoveredIndex = globalIndex"
            >
              <span class="doughnut-dot" :style="{ backgroundColor: color }"></span>
              <span class="doughnut-label" :title="label">{{ label }}</span>
              <span v-if="amount > 0" class="doughnut-val">{{ amount }}</span>
            </div>
          </div>
        </div>

        <!-- SVG Donut Chart -->
        <div class="doughnut-canvas-wrap">
          <svg class="doughnut-svg" viewBox="0 0 130 130">
            <!-- Background base track -->
            <circle
              cx="65"
              cy="65"
              r="46"
              fill="none"
              stroke="var(--app-border, rgba(120, 130, 145, 0.2))"
              stroke-width="17"
            />

            <!-- Donut Segments -->
            <g transform="rotate(-90 65 65)">
              <circle
                v-for="seg in donutSegments"
                :key="'seg-' + seg.index"
                cx="65"
                cy="65"
                r="46"
                fill="none"
                :stroke="seg.color"
                :stroke-width="hoveredIndex === seg.index ? 21 : 17"
                :stroke-dasharray="seg.dashArray"
                :stroke-dashoffset="seg.dashOffset"
                class="doughnut-arc"
                :class="{ 'is-active': hoveredIndex === seg.index }"
                @mouseenter="hoveredIndex = seg.index"
              />
            </g>

            <!-- Center Info -->
            <g class="doughnut-center-text">
              <text
                x="65"
                :y="activeItem ? 58 : 61"
                text-anchor="middle"
                class="center-val"
              >{{ centerValue }}</text>
              <text
                x="65"
                :y="activeItem ? 74 : 76"
                text-anchor="middle"
                class="center-sub"
              >{{ centerSub }}</text>
            </g>
          </svg>
        </div>
      </div>

      <chart-empty-state
        v-else
        height="140px"
        icon="fas fa-chart-pie"
      />
    </div>
  </div>
</template>

<script>
import ChartEmptyState from "@/project/Global/Components/ChartEmptyState.vue";

const FALLBACK_COLORS = ["#FF4560", "#008FFB", "#FEB019", "#775DD0", "#00E396", "#00D4FF", "#FF66C4", "#8D6E63", "#4ECDC4", "#546E7A"];
const R = 46;
const CIRCUMFERENCE = 2 * Math.PI * R; // ~289.027

export default {
  name: "ProgressDoughnutChart",
  components: { ChartEmptyState },
  props: {
    title: { type: String, default: "Göndəriş növləri üzrə bölgü" },
    count: { type: [Number, String], default: "" },
    chart: { type: Object, required: true },
  },
  data() {
    return {
      hoveredIndex: -1,
    };
  },
  computed: {
    items() {
      const mainLabels = this.chart?.mainLables || [];
      const data = this.chart?.datasets?.data || [];
      const len = Math.max(mainLabels.length, data.length);
      const list = [];
      for (let i = 0; i < len; i++) {
        const item = mainLabels[i] || {};
        const amount = Number(data[i]) || 0;
        list.push({
          index: i,
          label: item.label || `Növ ${i + 1}`,
          color: item.color || FALLBACK_COLORS[i % FALLBACK_COLORS.length],
          amount,
        });
      }
      return list;
    },
    column1Items() {
      const half = Math.ceil(this.items.length / 2);
      return this.items.slice(0, half);
    },
    column2Items() {
      const half = Math.ceil(this.items.length / 2);
      return this.items.slice(half).map((item, idx) => ({
        ...item,
        globalIndex: half + idx,
      }));
    },
    totalAmount() {
      return this.items.reduce((sum, item) => sum + item.amount, 0);
    },
    hasData() {
      return this.totalAmount > 0;
    },
    donutSegments() {
      const total = this.totalAmount;
      if (total <= 0) return [];

      let currentOffset = 0;
      const validItems = this.items.filter((item) => item.amount > 0);
      const gap = validItems.length > 1 ? 2.5 : 0; // small gap between segments

      return validItems.map((item) => {
        const pct = item.amount / total;
        const arcLength = Math.max(1, pct * CIRCUMFERENCE - gap);
        const dashArray = `${arcLength.toFixed(2)} ${CIRCUMFERENCE.toFixed(2)}`;
        const dashOffset = (-currentOffset).toFixed(2);
        currentOffset += pct * CIRCUMFERENCE;

        return {
          index: item.index,
          label: item.label,
          color: item.color,
          amount: item.amount,
          pct: Math.round(pct * 100),
          dashArray,
          dashOffset,
        };
      });
    },
    activeItem() {
      if (this.hoveredIndex >= 0 && this.hoveredIndex < this.items.length) {
        const it = this.items[this.hoveredIndex];
        if (it && it.amount > 0) return it;
      }
      return null;
    },
    centerValue() {
      if (this.activeItem) {
        return `${this.activeItem.amount}`;
      }
      return `${this.totalAmount}`;
    },
    centerSub() {
      if (this.activeItem) {
        const pct = this.totalAmount > 0 ? Math.round((this.activeItem.amount / this.totalAmount) * 100) : 0;
        return `${pct}%`;
      }
      return "Cəmi";
    },
  },
};
</script>

<style scoped>
.doughnut-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.35rem 0;
  user-select: none;
}

.doughnut-legend {
  display: flex;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
}

.doughnut-legend-col {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex: 1;
  min-width: 0;
}

.doughnut-legend-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  transition: opacity 0.2s ease, transform 0.15s ease, background-color 0.15s ease;
}

.doughnut-legend-item.is-hovered {
  background: var(--app-surface-soft, rgba(120, 130, 145, 0.1));
  transform: translateX(2px);
}

.doughnut-legend-item.is-dimmed {
  opacity: 0.35;
}

.doughnut-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex: 0 0 7px;
}

.doughnut-label {
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--app-text, #4b5563);
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.doughnut-val {
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--app-heading, #111827);
  margin-left: 2px;
}

.doughnut-canvas-wrap {
  width: 130px;
  height: 130px;
  flex: 0 0 130px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.doughnut-svg {
  width: 130px;
  height: 130px;
  display: block;
}

.doughnut-arc {
  transition: stroke-width 0.2s ease, opacity 0.2s ease;
  cursor: pointer;
}

.doughnut-arc.is-active {
  filter: drop-shadow(0 0 4px rgba(0, 0, 0, 0.3));
}

.center-val {
  font-size: 16px;
  font-weight: 800;
  fill: var(--app-heading, #111827);
  font-family: inherit;
}

.center-sub {
  font-size: 10px;
  font-weight: 600;
  fill: var(--app-text, #6b7280);
  font-family: inherit;
}
</style>
